import {
  GamePlayData,
  AutomationEnum,
  JobEnum,
  LocationEnum,
  SkillEnum,
  UnlockRule,
  QuestEnum,
  UpgradeEnum,
  UpgradeBoardEnum,
  CompetitionEnum
} from '../types'
import { JOBS } from '../constants/jobs'
import LOCATIONS from '../constants/locations'
import { SURF_SPOTS } from '../constants/surfSpots'
import { checkUnlockRules } from '../utils'
import { QUESTS } from '../constants/quests'
import { MIN_ENERGY_FOR_ACTIVITY_START, SKILL_LEVEL_CAP, BASE_MAX_ENERGY } from '../constants/game'
import { getTravelCostsFromCurrentLocation } from '../constants/travelCosts'
import { UPGRADES } from '../constants/upgrades'
import { isUnlockRuleMet } from '../utils'
import { completeQuestAsAutomation, addQuestToTimelineForAutomation } from './questCompletion'
import SKILL_TREE from '../constants/skillTree'
import { getEquipmentChangeData } from './equipment'
import { generateEnrollData, getStartHeatData } from './competition'

export function setAutomation(
  gameData: GamePlayData,
  automationKey: AutomationEnum,
  setActive: boolean = true,
  key?: string | undefined,
  value?: number | undefined
): GamePlayData {
  return {
    ...gameData,
    automations: {
      ...gameData.automations,
      [automationKey]: {
        ...gameData.automations[automationKey],
        isActive: setActive,
        ...(key !== undefined ? { key } : {}),
        ...(value !== undefined ? { value } : {})
      }
    }
  }
}

function findBestJobForMoney(gameData: GamePlayData): JobEnum | undefined {
  let bestJob: JobEnum | undefined
  let maxIncome = 0

  for (const [jobKey, job] of Object.entries(JOBS)) {
    if (checkUnlockRules(job.unlockRules, gameData) && job.income > maxIncome) {
      bestJob = jobKey as JobEnum
      maxIncome = job.income
    }
  }

  return bestJob
}

function findBestJobForRespect(gameData: GamePlayData): JobEnum | undefined {
  let bestJob: JobEnum | undefined
  let maxRespect = 0

  for (const [jobKey, job] of Object.entries(JOBS)) {
    if (checkUnlockRules(job.unlockRules, gameData) && job.respectIncome && job.respectIncome > maxRespect) {
      bestJob = jobKey as JobEnum
      maxRespect = job.respectIncome
    }
  }

  return bestJob
}

function findBestSpotForSkill(gameData: GamePlayData, skillKey: SkillEnum): string | undefined {
  let bestSpot: string | undefined
  let maxMultiplier = 0

  const spots = LOCATIONS[gameData.currentLocation].surfSpots
  for (const spot of spots) {
    const spotData = SURF_SPOTS[spot]
    const skillMultiplier = spotData.find((m) => m.key === skillKey)?.value || 1
    if (skillMultiplier > maxMultiplier) {
      bestSpot = spot
      maxMultiplier = skillMultiplier
    }
  }

  return bestSpot
}

function findBestSurfboardForSkill(gameData: GamePlayData, skill?: SkillEnum): UpgradeBoardEnum | undefined {
  const ownedBoards = Object.values(UPGRADES).filter(
    (upgrade) => upgrade.category === 'surfboards' && gameData.upgrades[upgrade.key]?.isPurchased
  )

  if (ownedBoards.length === 0) return undefined

  if (!skill) {
    // If no specific skill is requested, return the last owned board
    return ownedBoards[ownedBoards.length - 1].key as UpgradeBoardEnum
  }

  // Find board with highest multiplier for the given skill
  let bestBoard: UpgradeBoardEnum | undefined
  let bestMultiplier = 0

  ownedBoards.forEach((board) => {
    const skillEffect = board.effects.find((effect) => effect.effectCategory === 'skill' && effect.key === skill)
    if (skillEffect && skillEffect.value > bestMultiplier) {
      bestMultiplier = skillEffect.value
      bestBoard = board.key as UpgradeBoardEnum
    }
  })

  return bestBoard || (ownedBoards[ownedBoards.length - 1].key as UpgradeBoardEnum)
}

export function handleLevelUpAutomation(gameData: GamePlayData): GamePlayData {
  const automation = gameData.automations.level_up
  if (!automation.isActive || !automation.key || !automation.value) return gameData

  let isSurfingGreenWave = gameData.currentLocation !== 'mediterranean' && automation.key !== 'whitewater_riding'
  let bestSpot: string | undefined

  if (automation.key === 'skill') {
    // Find the skill tree level that matches our target value
    const targetLevel = SKILL_TREE[automation.value]
    if (!targetLevel) return gameData

    // Check each skill requirement and find the first one that's not met
    for (const [skillKey, requiredLevel] of Object.entries(targetLevel.unlockRequirements)) {
      const currentLevel = gameData.skills[skillKey as SkillEnum]?.level || 0
      if (currentLevel < requiredLevel) {
        // Found a skill that needs leveling - find the best spot for it
        bestSpot = findBestSpotForSkill(gameData, skillKey as SkillEnum)
        isSurfingGreenWave = skillKey !== 'whitewater_riding'
        break // Exit loop after finding first skill that needs improvement
      }
    }
  } else {
    // Individual skill leveling
    bestSpot = findBestSpotForSkill(gameData, automation.key as SkillEnum)
  }

  let newData = gameData
  if (bestSpot && bestSpot !== gameData.currentLocationSpot) {
    newData = {
      ...gameData,
      isSurfingGreenWave,
      currentLocationSpot: bestSpot
    }
  }

  // Enable both work and surf for skill leveling
  newData = setAutomation(newData, 'work', true)
  return setAutomation(newData, 'surf', true)
}

export function handleMoneySaveAutomation(gameData: GamePlayData): GamePlayData {
  const automation = gameData.automations.money_save
  if (!automation.isActive || !automation.value) return gameData

  const bestJob = findBestJobForMoney(gameData)
  if (!bestJob) return gameData

  if (bestJob !== gameData.currentJob) {
    let newData = {
      ...gameData,
      currentJob: bestJob
    } as GamePlayData
    // Enable work and disable surf for money saving
    newData = setAutomation(newData, 'surf', false)
    return setAutomation(newData, 'work', true)
  }

  // Enable work and disable surf for money saving
  let newData = setAutomation(gameData, 'surf', false)
  return setAutomation(newData, 'work', true)
}

const findFirstUnmetUnlockRule = (
  rules: UnlockRule[],
  gameData: GamePlayData,
  depth: number = 0,
  visited: Set<string> = new Set()
): UnlockRule | null => {
  // Prevent infinite loops
  if (depth > 20) return null

  // First, count how many unmet rules we have
  const unmetRules = rules.filter((rule) => {
    const ruleId = `${rule.category}:${rule.key}`
    if (visited.has(ruleId)) return false
    visited.add(ruleId)
    return !isUnlockRuleMet(rule, gameData)
  })

  if (unmetRules.length === 0) {
    return null // All rules are met
  }

  // Prioritize certain rule categories
  // First prioritize skills (levels) since these need to be built up first
  const skillRule = unmetRules.find((rule) => rule.category === 'level')
  if (skillRule) return skillRule

  // Then check for job requirements
  const jobRule = unmetRules.find((rule) => rule.category === 'job')
  if (jobRule) {
    // Check if job needs prerequisites
    const job = JOBS[jobRule.key as JobEnum]
    const prerequisiteRule = findFirstUnmetUnlockRule(job.unlockRules, gameData, depth + 1, visited)
    // If there's a prerequisite rule that's not met, return that instead
    if (prerequisiteRule) return prerequisiteRule
    return jobRule
  }

  // Then upgrade/purchase requirements
  const purchaseRule = unmetRules.find((rule) => rule.category === 'purchase')
  if (purchaseRule) {
    // Check if purchase needs prerequisites
    const upgrade = UPGRADES[purchaseRule.key as UpgradeEnum]
    const prerequisiteRule = findFirstUnmetUnlockRule(upgrade.visibilityRules, gameData, depth + 1, visited)
    // If there's a prerequisite rule that's not met, return that instead
    if (prerequisiteRule) return prerequisiteRule
    return purchaseRule
  }

  // Then check for location requirements
  const locationRule = unmetRules.find((rule) => rule.category === 'location')
  if (locationRule) return locationRule

  // Then currency requirements
  const currencyRule = unmetRules.find((rule) => rule.category === 'currency')
  if (currencyRule) return currencyRule

  // Then stat requirements
  const statRule = unmetRules.find((rule) => rule.category === 'local_stat' || rule.category === 'global_stat')
  if (statRule) return statRule

  // Finally competition requirements
  const contestRule = unmetRules.find((rule) => rule.category === 'contest')
  if (contestRule) return contestRule

  // If we get here, just return the first unmet rule
  return unmetRules[0]
}

const handleJobChangeAutomation = (gameData: GamePlayData): GamePlayData => {
  const automation = gameData.automations.job_change
  if (!automation.isActive || !automation.key) return gameData

  const targetJob = automation.key as JobEnum
  const job = JOBS[targetJob]
  const isJobUnlocked = job.unlockRules.every((rule) => isUnlockRuleMet(rule, gameData))
  let newData = setAutomation(gameData, 'surf', false)
  newData = setAutomation(newData, 'work', true)

  if (isJobUnlocked) {
    // If job is unlocked, switch to it
    return {
      ...newData,
      currentJob: targetJob
    }
  }

  // Find the first unmet prerequisite rule
  const unmetRule = findFirstUnmetUnlockRule(job.unlockRules, gameData)
  if (unmetRule) {
    switch (unmetRule.category) {
      case 'level':
        return setAutomation(newData, 'level_up', true, unmetRule.key, unmetRule.value as number)
      case 'currency':
        return setAutomation(newData, 'money_save', true, undefined, unmetRule.value as number)
      case 'purchase':
        return setAutomation(newData, 'purchase', true, unmetRule.key)
      case 'job':
        return setAutomation(newData, 'job_change', true, unmetRule.key)
      case 'location':
        return setAutomation(newData, 'location_change', true, unmetRule.key)
      case 'local_stat':
      case 'global_stat':
        return setAutomation(newData, 'stat_reach', true, unmetRule.key, unmetRule.value as number)
      case 'contest':
        return setAutomation(newData, 'contest_reach', true, unmetRule.key, unmetRule.value as number)
    }
  }

  // If no unmet rules found but job is still locked, something is wrong
  return gameData
}

export function handleLocationChangeAutomation(gameData: GamePlayData): GamePlayData {
  const automation = gameData.automations.location_change
  if (!automation.isActive || !automation.key) return gameData

  const targetLocation = automation.key as LocationEnum
  const location = LOCATIONS[targetLocation]
  const travelCost = getTravelCostsFromCurrentLocation(gameData, targetLocation)
  const totalCostNeeded = travelCost + location.surfSessionCost

  if (gameData.currencies.money.amount < totalCostNeeded) {
    return setAutomation(gameData, 'money_save', true, undefined, totalCostNeeded)
  }

  // Get the first available surf spot in the new location
  const firstSpot = location.surfSpots[0]
  const newData = setAutomation(gameData, 'location_change', false)
  return {
    ...newData,
    currentLocation: targetLocation,
    currentLocationSpot: firstSpot,
    currencies: {
      ...gameData.currencies,
      money: {
        ...gameData.currencies.money,
        amount: gameData.currencies.money.amount - travelCost
      }
    }
  }
}

export function handleContestReachAutomation(gameData: GamePlayData): GamePlayData {
  const automation = gameData.automations.contest_reach
  if (!automation.isActive || !automation.key || !automation.value) return gameData

  const contestKey = automation.key as CompetitionEnum
  const targetPlacement = automation.value as number

  // First, check if we need to change location to where the competition is held
  const competitionLocation = Object.entries(LOCATIONS).find(([_, locationData]) =>
    locationData.competitions?.some((comp) => comp.key === contestKey)
  )

  if (!competitionLocation) return gameData // Competition not found

  const [locationKey, locationData] = competitionLocation

  // If player is not in the right location, change location first
  if (gameData.currentLocation !== locationKey) {
    return setAutomation(gameData, 'location_change', true, locationKey)
  }

  // Find the competition
  const competition = locationData.competitions?.find((comp) => comp.key === contestKey)
  if (!competition) return gameData

  // Check if we have enough money for the entry fee
  if (gameData.currencies.money.amount < competition.entryFee) {
    return setAutomation(gameData, 'money_save', true, undefined, competition.entryFee)
  }

  // Check if player is already in a competition
  if (gameData.currentCompetition) {
    // If it's not our target competition, cancel it
    if (gameData.currentCompetition.key !== contestKey) {
      const newData = {
        ...gameData,
        currentCompetition: undefined
      }
      return handleContestReachAutomation(newData) // Retry with the correct competition
    }

    // If player is in the target competition, check if they're in a heat they need to surf
    const currentRound = gameData.currentCompetition.rounds[gameData.currentCompetition.currentRoundIndex]
    const currentHeat = currentRound?.heats[gameData.currentCompetition.currentHeatIndex]
    const isPlayerInHeat = currentHeat?.competitors.some((name) => name === gameData.username) ?? false

    if (isPlayerInHeat) {
      // Start the heat using the competition engine
      return getStartHeatData(gameData)
    }

    // If competition is complete, check the placement
    if (gameData.currentCompetition.isComplete) {
      const playerResult = gameData.currentCompetition.competitorMap[gameData.username]

      // If player reached target placement or better, turn off automation
      if (playerResult && playerResult.position && playerResult.position <= targetPlacement) {
        return setAutomation(gameData, 'contest_reach', false)
      } else {
        // Otherwise, wait for competition to become available again
        // Enable surf and work automations in the meantime
        let newData = setAutomation(gameData, 'surf', true)
        newData = setAutomation(newData, 'work', true)
        // Clear current competition to wait for next opportunity
        return {
          ...newData,
          currentCompetition: undefined
        }
      }
    }

    // Competition is ongoing but player is not in current heat,
    // just continue and wait for next heat assignment
    return gameData
  }

  // Check if competition is available based on the last played day
  const competitionStats = gameData.competitionStats[contestKey]
  const lastPlayedDay = competitionStats?.lastCompetitionGameDay || 0

  if (lastPlayedDay > 0 && competition.restartIntervalDays !== undefined) {
    const currentDay = Math.floor(gameData.gameMinutes / (60 * 24))
    const daysSinceLastPlay = currentDay - lastPlayedDay

    if (daysSinceLastPlay < competition.restartIntervalDays) {
      // Competition is not yet available, enable surf and work automations in the meantime
      let newData = setAutomation(gameData, 'surf', true)
      newData = setAutomation(newData, 'work', true)
      return newData
    }
  }

  // If we get here, player is ready to enter the competition
  return generateEnrollData(gameData, contestKey)
}

export function handlePurchaseAutomation(gameData: GamePlayData): GamePlayData {
  const automation = gameData.automations.purchase
  if (!automation.isActive || !automation.key) return gameData

  const upgradeKey = automation.key as UpgradeEnum
  const upgrade = UPGRADES[upgradeKey]

  // Check if we already own this upgrade
  if (gameData.upgrades[upgradeKey]?.isPurchased) {
    return setAutomation(gameData, 'purchase', false)
  }

  // Check visibility rules first
  if (!checkUnlockRules(upgrade.visibilityRules, gameData)) {
    // Find first unmet visibility rule and set appropriate automation
    for (const rule of upgrade.visibilityRules) {
      if (!checkUnlockRules([rule], gameData)) {
        switch (rule.category) {
          case 'level':
            return setAutomation(gameData, 'level_up', true, rule.key, rule.value as number)
          case 'job':
            return setAutomation(gameData, 'job_change', true, rule.key)
          case 'location':
            return setAutomation(gameData, 'location_change', true, rule.key)
          case 'currency':
            return setAutomation(gameData, 'money_save', true, undefined, rule.value as number)
          case 'purchase':
            // If we need another upgrade first, set that as our new purchase target
            return setAutomation(gameData, 'purchase', true, rule.key)
          case 'local_stat':
          case 'global_stat':
            return setAutomation(gameData, 'stat_reach', true, rule.key, rule.value as number)
          case 'contest':
            // Set contest_reach automation instead of disabling the purchase
            return setAutomation(gameData, 'contest_reach', true, rule.key, rule.value as number)
        }
      }
    }
  }

  // If we can see the upgrade but don't have enough money, save for it
  if (gameData.currencies.money.amount < upgrade.cost) {
    return setAutomation(gameData, 'money_save', true, undefined, upgrade.cost)
  }

  // If we have enough money and meet all requirements, purchase the upgrade
  let newData = {
    ...gameData,
    upgrades: {
      ...gameData.upgrades,
      [upgradeKey]: {
        isPurchased: true
      }
    },
    currencies: {
      ...gameData.currencies,
      money: {
        ...gameData.currencies.money,
        amount: gameData.currencies.money.amount - upgrade.cost
      }
    }
  }

  return setAutomation(newData, 'purchase', false)
}

export function handleStatReachAutomation(gameData: GamePlayData): GamePlayData {
  const automation = gameData.automations.stat_reach
  if (!automation.isActive || !automation.key || !automation.value) return gameData

  if (automation.key === 'respectTotal') {
    const bestJob = findBestJobForRespect(gameData)
    if (!bestJob) return gameData

    if (bestJob !== gameData.currentJob) {
      let newData = {
        ...gameData,
        currentJob: bestJob
      } as GamePlayData
      // Enable both work and surf for respect earning
      newData = setAutomation(newData, 'surf', true)
      return setAutomation(newData, 'work', true)
    }
  }

  // Enable both work and surf for stat reaching
  let newData = setAutomation(gameData, 'work', true)
  return setAutomation(newData, 'surf', true)
}

export function stopAllHigherAutomations(gameData: GamePlayData): GamePlayData {
  let newData = gameData
  newData = setAutomation(newData, 'level_up', false)
  newData = setAutomation(newData, 'job_change', false)
  newData = setAutomation(newData, 'money_save', false)
  newData = setAutomation(newData, 'location_change', false)
  newData = setAutomation(newData, 'purchase', false)
  newData = setAutomation(newData, 'stat_reach', false)
  newData = setAutomation(newData, 'quest_complete', false)
  newData = setAutomation(newData, 'contest_reach', false)
  return newData
}

export function handleQuestCompleteAutomation(gameData: GamePlayData): GamePlayData {
  const automation = gameData.automations.quest_complete
  if (!automation.isActive || !automation.key) return gameData

  const questKey = automation.key as QuestEnum
  const quest = QUESTS[questKey]

  // If the quest is not in the current location, change location
  if (quest.location !== gameData.currentLocation) {
    return setAutomation(gameData, 'location_change', true, quest.location)
  }

  // Check if the quest can be completed (all unlock requirements are met)
  if (checkUnlockRules(quest.unlockRequirements, gameData)) {
    // If all requirements are met, complete the quest
    let newData = completeQuestAsAutomation(gameData, questKey)

    // Add quest and related story items to the timeline (simulating manual completion)
    newData = addQuestToTimelineForAutomation(newData, questKey)

    return stopAllHigherAutomations(newData)
  }

  // If not all requirements are met, find the highest priority unmet rule
  const unmetRule = findFirstUnmetUnlockRule(quest.unlockRequirements, gameData)
  if (unmetRule) {
    // Debug logging to see what rule is being processed
    console.log(`Working on requirement: ${unmetRule.category} - ${unmetRule.key} - ${unmetRule.value}`)

    switch (unmetRule.category) {
      case 'level':
        return setAutomation(gameData, 'level_up', true, unmetRule.key, unmetRule.value as number)
      case 'currency':
        return setAutomation(gameData, 'money_save', true, undefined, unmetRule.value as number)
      case 'purchase':
        return setAutomation(gameData, 'purchase', true, unmetRule.key)
      case 'job':
        return setAutomation(gameData, 'job_change', true, unmetRule.key)
      case 'location':
        return setAutomation(gameData, 'location_change', true, unmetRule.key)
      case 'local_stat':
      case 'global_stat':
        return setAutomation(gameData, 'stat_reach', true, unmetRule.key, unmetRule.value as number)
      case 'contest':
        return setAutomation(gameData, 'contest_reach', true, unmetRule.key, unmetRule.value as number)
    }
  }

  // If we get here but the quest can't be completed, we might have an edge case
  console.log("No unmet rules found but quest can't be completed. Edge case detected.")
  return gameData
}

export function performAutomations(gameData: GamePlayData): GamePlayData {
  let updatedData = { ...gameData }

  // Handle automations in order of priority
  if (updatedData.automations.quest_complete.isActive) {
    updatedData = handleQuestCompleteAutomation(updatedData)
  }
  if (updatedData.automations.level_up.isActive) {
    updatedData = handleLevelUpAutomation(updatedData)
  }
  if (updatedData.automations.job_change.isActive) {
    updatedData = handleJobChangeAutomation(updatedData)
  }
  if (updatedData.automations.money_save.isActive) {
    updatedData = handleMoneySaveAutomation(updatedData)
  }
  if (updatedData.automations.location_change.isActive) {
    updatedData = handleLocationChangeAutomation(updatedData)
  }
  if (updatedData.automations.purchase.isActive) {
    updatedData = handlePurchaseAutomation(updatedData)
  }
  if (updatedData.automations.stat_reach.isActive) {
    updatedData = handleStatReachAutomation(updatedData)
  }
  if (updatedData.automations.contest_reach.isActive) {
    updatedData = handleContestReachAutomation(updatedData)
  }

  // Basic activity automations
  const { automations, currencies } = updatedData
  const maxEnergy = currencies.energy.max || BASE_MAX_ENERGY * SKILL_LEVEL_CAP
  const isEnergyFull = currencies.energy.amount >= maxEnergy
  const surfCost = LOCATIONS[updatedData.currentLocation].surfSessionCost
  const hasEnoughMoneyForSurf = currencies.money.amount >= surfCost

  if (isEnergyFull && currencies.energy.amount >= MIN_ENERGY_FOR_ACTIVITY_START) {
    if (automations.surf.isActive && hasEnoughMoneyForSurf) {
      if (!updatedData.equipment.surfboard) {
        const bestBoard = findBestSurfboardForSkill(updatedData)
        updatedData = getEquipmentChangeData(updatedData, 'surfboard', bestBoard)
      }
      updatedData.currentActivity = 'surf'
    } else if (automations.work.isActive) {
      updatedData.currentActivity = 'work'
    }
  } else if (currencies.energy.amount <= 0 && automations.rest.isActive) {
    updatedData.currentActivity = 'rest'
  }

  return updatedData
}
